import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import * as lostReasonsApi from './lost-reasons.api';

import type {
  CreateLostReasonRequest,
  LostReasonListQuery,
  UpdateLostReasonRequest,
} from '@utazzo/shared';

import { extractServerError } from '@/lib/api-error';

const QK = {
  list: (query: LostReasonListQuery) => ['lost-reasons', 'list', query] as const,
  detail: (id: string) => ['lost-reasons', 'detail', id] as const,
};

export const useLostReasonsList = (query: LostReasonListQuery) =>
  useQuery({
    queryKey: QK.list(query),
    queryFn: () => lostReasonsApi.listLostReasons(query),
    staleTime: 30_000,
  });

export const useCreateLostReason = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateLostReasonRequest) => lostReasonsApi.createLostReason(payload),
    onSuccess: (lr) => {
      qc.invalidateQueries({ queryKey: ['lost-reasons', 'list'] });
      toast.success(`Added ${lr.name}`);
    },
    onError: (err: unknown) =>
      toast.error(extractServerError(err).message ?? 'Failed to add lost reason'),
  });
};

export const useUpdateLostReason = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateLostReasonRequest }) =>
      lostReasonsApi.updateLostReason(id, payload),
    onSuccess: (lr) => {
      qc.invalidateQueries({ queryKey: ['lost-reasons', 'list'] });
      qc.invalidateQueries({ queryKey: QK.detail(lr.id) });
      toast.success('Lost reason updated');
    },
    onError: (err: unknown) =>
      toast.error(extractServerError(err).message ?? 'Failed to update lost reason'),
  });
};

export const useDeleteLostReason = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => lostReasonsApi.deleteLostReason(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['lost-reasons', 'list'] });
      toast.success('Lost reason deleted');
    },
    onError: (err: unknown) =>
      toast.error(extractServerError(err).message ?? 'Failed to delete lost reason'),
  });
};

export const useBulkDeleteLostReasons = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => lostReasonsApi.bulkDeleteLostReasons(ids),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['lost-reasons', 'list'] }),
    onError: (err: unknown) =>
      toast.error(extractServerError(err).message ?? 'Failed to delete lost reasons'),
  });
};

export const useBulkSetLostReasonsStatus = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ ids, isActive }: { ids: string[]; isActive: boolean }) =>
      lostReasonsApi.bulkSetLostReasonsStatus(ids, isActive),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['lost-reasons', 'list'] }),
    onError: (err: unknown) =>
      toast.error(extractServerError(err).message ?? 'Failed to update lost reason status'),
  });
};
